import type { ContentMedia, FounderStage } from '@doneverse/shared'
import { api } from '../api/index.js'

export type CoachingRecordingBucket = 'doneverse' | 'pre_doneverse'

export interface CoachingCallRecordingListItem {
  id: string
  bucket: CoachingRecordingBucket
  page_slug: string
  title: string
  byline: string | null
  recorded_at: string | null
  month_key: string
  excerpt: string | null
  hero_asset_url: string | null
  media: ContentMedia[]
  matched_terms?: string[]
}

export interface CoachingCallRecordingDetail {
  id: string
  bucket: CoachingRecordingBucket
  page_slug: string
  course_id: string
  month_key: string
  title: string
  body: string
  byline: string | null
  recorded_at: string | null
  extras: Record<string, unknown>
  media: ContentMedia[]
  created_at: string
  updated_at: string
}

export interface ListRecordingsResponse {
  items: CoachingCallRecordingListItem[]
}

export interface MonthBucketCount {
  bucket: CoachingRecordingBucket
  page_slug: string
  month_key: string
  count: number
}

export interface ListRecordingsFilters {
  bucket?: CoachingRecordingBucket
  month?: string
  limit?: number
  q?: string
}

// ─── Admin shapes ────────────────────────────────────────────────────────
// Admin payloads still speak the dashboard's bucket/vimeo/hero terminology;
// the server translates that into content_lesson + content_media rows.

export interface AdminCoachingCallRecording {
  id: string
  bucket: CoachingRecordingBucket
  page_slug: string
  course_id: string
  month_key: string
  title: string
  coach_name: string | null
  recorded_at: string | null
  vimeo_video_id: string | null
  // Raw external hero URL (may be null when the thumbnail is stored in R2).
  hero_image_url: string | null
  // Raw R2 key for an uploaded thumbnail; the form round-trips this on save.
  hero_image_r2_key: string | null
  // Signed (or external-fallback) URL for previewing the current thumbnail.
  hero_preview_url: string | null
  content_markdown: string
  founder_stages: FounderStage[]
  created_at: string
  updated_at: string
}

export interface AdminCoachingCallRecordingListItem {
  id: string
  bucket: CoachingRecordingBucket
  page_slug: string
  title: string
  coach_name: string | null
  recorded_at: string | null
  month_key: string
  source_section_title: string | null
  vimeo_video_id: string | null
  hero_asset_url: string | null
  excerpt: string | null
  founder_stages: FounderStage[]
}

export interface AdminListRecordingsResponse {
  items: AdminCoachingCallRecordingListItem[]
}

export interface AdminCoachingCallRecordingCreateInput {
  bucket: CoachingRecordingBucket
  title: string
  month_key: string
  coach_name?: string | null
  recorded_at?: string | null
  vimeo_video_id?: string | null
  hero_image_url?: string | null
  hero_image_r2_key?: string | null
  content_markdown?: string
  founder_stages?: FounderStage[]
}

export type AdminCoachingCallRecordingUpdateInput = Partial<AdminCoachingCallRecordingCreateInput>

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    const str = String(value)
    if (str.length === 0) continue
    search.set(key, str)
  }
  const str = search.toString()
  return str ? `?${str}` : ''
}

export const coachingCallRecordingsService = {
  list: async (filters: ListRecordingsFilters = {}): Promise<ListRecordingsResponse> => {
    const qs = buildQuery({
      bucket: filters.bucket,
      month: filters.month,
      limit: filters.limit,
      q: filters.q,
    })
    return api.get(`/coaching-calls/recordings${qs}`).json<ListRecordingsResponse>()
  },

  get: async (id: string): Promise<CoachingCallRecordingDetail> => {
    return api.get(`/coaching-calls/recordings/${encodeURIComponent(id)}`).json<CoachingCallRecordingDetail>()
  },

  months: async (bucket?: CoachingRecordingBucket): Promise<MonthBucketCount[]> => {
    const qs = buildQuery({ bucket })
    return api.get(`/coaching-calls/recordings/months${qs}`).json<MonthBucketCount[]>()
  },

  // ─── Admin endpoints (require role=admin) ─────────────────────────────
  adminList: async (): Promise<AdminListRecordingsResponse> => {
    return api.get('/v1/admin/coaching-call-recordings').json<AdminListRecordingsResponse>()
  },

  adminGet: async (id: string): Promise<AdminCoachingCallRecording> => {
    return api
      .get(`/v1/admin/coaching-call-recordings/${encodeURIComponent(id)}`)
      .json<AdminCoachingCallRecording>()
  },

  adminCreate: async (input: AdminCoachingCallRecordingCreateInput): Promise<AdminCoachingCallRecording> => {
    return api.post(input, '/v1/admin/coaching-call-recordings').json<AdminCoachingCallRecording>()
  },

  adminUpdate: async (
    id: string,
    patch: AdminCoachingCallRecordingUpdateInput,
  ): Promise<AdminCoachingCallRecording> => {
    return api
      .patch(patch, `/v1/admin/coaching-call-recordings/${encodeURIComponent(id)}`)
      .json<AdminCoachingCallRecording>()
  },

  adminDelete: async (id: string): Promise<void> => {
    await api.delete(`/v1/admin/coaching-call-recordings/${encodeURIComponent(id)}`).res()
  },

  // Upload a thumbnail image; the raw file bytes are the request body (an image
  // Content-Type, not JSON/multipart — the API reads it via express.raw).
  // Returns the R2 key (send back as `hero_image_r2_key`) + a signed preview.
  adminUploadThumbnail: async (file: File): Promise<{ r2_key: string; preview_url: string }> => {
    return api
      .url('/v1/admin/coaching-call-recordings/thumbnail')
      .content(file.type || 'application/octet-stream')
      .body(file)
      .post()
      .json<{ r2_key: string; preview_url: string }>()
  },
}
